import type { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { EmptyState } from './EmptyState'
import { cn } from '../lib/cn'

export interface DataTableColumn<T> {
  key: string
  header: string
  render: (row: T) => ReactNode
  className?: string
  headerClassName?: string
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  data: T[]
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  emptyTitle?: string
  emptyDescription?: string
  minWidth?: number
}

/**
 * Generic sortable-free data table used across list pages (Clients, Team
 * Management, Workspace Settings). Pass typed columns rather than hand-rolling
 * <table> markup per page — keeps row hover/stripe/empty-state behavior consistent.
 */
export function DataTable<T>({
  columns,
  data,
  rowKey,
  onRowClick,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  minWidth = 640,
}: DataTableProps<T>) {
  if (data.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />
  }

  return (
    <div className="overflow-x-auto rounded-card border border-shell-border bg-shell shadow-card scrollbar-thin">
      <table className="w-full text-left text-sm" style={{ minWidth }}>
        <thead>
          <tr className="border-b border-shell-border text-xs font-semibold uppercase tracking-wide text-shell-text">
            {columns.map((col) => (
              <th key={col.key} className={cn('px-5 py-3', col.headerClassName)}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <motion.tr
              key={rowKey(row)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: Math.min(i * 0.02, 0.3) }}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                'border-b border-shell-border/60 last:border-0 transition-colors hover:bg-shell-border/30',
                onRowClick && 'cursor-pointer',
              )}
            >
              {columns.map((col) => (
                <td key={col.key} className={cn('px-5 py-3.5', col.className)}>
                  {col.render(row)}
                </td>
              ))}
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
